// SMS IA - Module de communication SMS
// Interface pour envoi, templates, règles et historique

import React, { useState, useEffect } from "react";
import axios from "axios";
import { getTenantHeaders } from "../../lib/utils";
import { 
  MessageCircle, Send, FileText, Settings, History, 
  Plus, Edit, Check, X, Play, Eye, Copy,
  AlertCircle, CheckCircle, Clock, XCircle, RefreshCw,
  ChevronDown, ChevronRight, Loader2, Search, Filter, Archive
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;

// Toutes les requêtes communication doivent porter le Bearer/tenant pour que
// le backend scope l'historique au tenant courant (OH-AUDIT-005). Sans ça,
// l'historique d'un tenant fuite chez les autres. Instance dédiée avec
// intercepteur → headers injectés sur CHAQUE appel (lu dynamiquement).
const api = axios.create();
api.interceptors.request.use((config) => {
  config.headers = { ...(config.headers || {}), ...getTenantHeaders() };
  return config;
});

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================

export default function SMSManager() {
  const [activeTab, setActiveTab] = useState("send");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // Charger les stats au montage
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get(`${API}/api/communication/stats?channel=sms`);
      setStats(res.data);
    } catch (err) {
      console.error("Erreur chargement stats:", err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">SMS IA</h1>
            <p className="text-sm text-zinc-400">Envoi, templates et historique</p>
          </div>
        </div>
        
        {/* Stats rapides */}
        {stats && (
          <div className="flex gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{stats.sent || 0}</p>
              <p className="text-xs text-zinc-500">Envoyés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.simulated || 0}</p>
              <p className="text-xs text-zinc-500">Simulés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-400">{stats.failed || 0}</p>
              <p className="text-xs text-zinc-500">Échoués</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-800/50 border border-white/10">
          <TabsTrigger value="send" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
            <Send className="w-4 h-4 mr-2" />
            Envoyer
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
            <Settings className="w-4 h-4 mr-2" />
            Règles
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
            <History className="w-4 h-4 mr-2" />
            Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="send" className="mt-4">
          <SendSMSPanel onSent={loadStats} />
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <TemplatesPanel />
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <RulesPanel />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <HistoryPanel onRefresh={loadStats} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// PANEL ENVOI SMS
// ============================================================

function SendSMSPanel({ onSent }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [variables, setVariables] = useState({});
  const [context, setContext] = useState("");
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.get(`${API}/api/communication/templates?channel=sms&status=active`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error("Erreur chargement templates:", err);
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    
    // Initialiser les variables
    if (template?.variables) {
      const vars = {};
      template.variables.forEach(v => {
        vars[v.name] = v.default_value || "";
      });
      setVariables(vars);
    }
    
    updatePreview(template, variables);
  };

  const updatePreview = (template, vars) => {
    if (!template) {
      setPreview("");
      return;
    }
    
    let content = template.content;
    Object.entries(vars).forEach(([key, value]) => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || `{{${key}}}`);
    });
    setPreview(content);
  };

  const handleVariableChange = (name, value) => {
    const newVars = { ...variables, [name]: value };
    setVariables(newVars);
    updatePreview(selectedTemplate, newVars);
  };

  const handleSend = async (simulate = false) => {
    if (!selectedTemplate || !recipient) {
      setResult({ success: false, error: "Template et destinataire requis" });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const endpoint = simulate ? "simulate" : "send";
      const res = await api.post(`${API}/api/communication/${endpoint}`, {
        channel: "sms",
        template_id: selectedTemplate.id,
        recipient: recipient,
        variables: variables,
        triggered_by: "human:support",
        context: context || "Envoi manuel depuis UI"
      });

      setResult(res.data);
      if (res.data.success && onSent) {
        onSent();
      }
    } catch (err) {
      setResult({ 
        success: false, 
        error: err.response?.data?.detail || err.message 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {/* Formulaire */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-400" />
            Envoyer un SMS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sélection template */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Template</label>
            <Select onValueChange={handleTemplateChange}>
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                <SelectValue placeholder="Choisir un template..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-white">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Destinataire */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Destinataire</label>
            <Input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="bg-zinc-800 border-white/10 text-white"
            />
          </div>

          {/* Variables */}
          {selectedTemplate?.variables?.length > 0 && (
            <div className="space-y-3">
              <label className="text-sm text-zinc-400 block">Variables</label>
              {selectedTemplate.variables.map(v => (
                <div key={v.name}>
                  <label className="text-xs text-zinc-500 mb-1 block">
                    {v.name} {v.required && <span className="text-red-400">*</span>}
                  </label>
                  <Input
                    value={variables[v.name] || ""}
                    onChange={(e) => handleVariableChange(v.name, e.target.value)}
                    placeholder={v.description || v.name}
                    className="bg-zinc-800 border-white/10 text-white"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Contexte */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Contexte (optionnel)</label>
            <Input
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Raison de l'envoi..."
              className="bg-zinc-800 border-white/10 text-white"
            />
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => handleSend(true)}
              disabled={loading || !selectedTemplate}
              variant="outline"
              className="flex-1 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Simuler
            </Button>
            <Button
              onClick={() => handleSend(false)}
              disabled={loading || !selectedTemplate}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Prévisualisation et Résultat */}
      <div className="space-y-4">
        {/* Preview */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Eye className="w-4 h-4 text-zinc-400" />
              Prévisualisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <div className="bg-zinc-800 rounded-lg p-4 text-white text-sm">
                {preview}
              </div>
            ) : (
              <p className="text-zinc-500 text-sm">Sélectionnez un template...</p>
            )}
          </CardContent>
        </Card>

        {/* Résultat */}
        {result && (
          <Card className={`border ${result.success ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.success 
                      ? (result.simulated ? "Simulation réussie" : "SMS envoyé")
                      : "Échec"
                    }
                  </p>
                  {result.success && (
                    <p className="text-xs text-zinc-400 mt-1">
                      ID: {result.message_id}
                    </p>
                  )}
                  {result.error && (
                    <p className="text-sm text-red-300 mt-1">{result.error}</p>
                  )}
                  {result.validation && !result.validation.is_valid && (
                    <div className="mt-2 text-xs text-zinc-400">
                      <p>Checks: {JSON.stringify(result.validation.checks)}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ============================================================
// PANEL TEMPLATES
// ============================================================

function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    content: "",
    category: "",
    tone: "professionnel"
  });
  const [variablesList, setVariablesList] = useState([]);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/communication/templates?channel=sms`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({ name: "", description: "", content: "", category: "", tone: "professionnel" });
    setVariablesList([]);
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      content: template.content,
      category: template.category || "",
      tone: template.tone || "professionnel"
    });
    setVariablesList(template.variables || []);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        channel: "sms",
        variables: variablesList
      };

      if (editingTemplate) {
        await api.put(`${API}/api/communication/templates/${editingTemplate.id}`, payload);
      } else {
        await api.post(`${API}/api/communication/templates?created_by=human:support`, payload);
      }

      setShowModal(false);
      loadTemplates();
    } catch (err) {
      console.error("Erreur:", err);
      alert(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    }
  };

  const handleToggleStatus = async (template) => {
    try {
      const endpoint = template.status === "active" ? "archive" : "activate";
      await api.post(`${API}/api/communication/templates/${template.id}/${endpoint}`);
      loadTemplates();
    } catch (err) {
      console.error("Erreur:", err);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Archiver le template "${template.name}" ?`)) return;
    
    try {
      await api.delete(`${API}/api/communication/templates/${template.id}`);
      toast.success("Template archivé dans Zoho");
      loadTemplates();
    } catch (err) {
      console.error("Erreur:", err);
      toast.error("Erreur lors de l'archivage");
    }
  };

  const extractVariables = (content) => {
    const matches = content.match(/\{\{(\w+)\}\}/g) || [];
    const varNames = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
    const uniqueVars = [...new Set(varNames)];
    
    setVariablesList(uniqueVars.map(name => ({
      name,
      required: true,
      description: ""
    })));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-500/20 text-emerald-400">Actif</Badge>;
      case "draft":
        return <Badge className="bg-yellow-500/20 text-yellow-400">Brouillon</Badge>;
      case "archived":
        return <Badge className="bg-zinc-500/20 text-zinc-400">Archivé</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Templates SMS</h2>
        <Button onClick={handleCreate} className="bg-blue-500 hover:bg-blue-600">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau template
        </Button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="py-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Aucun template SMS</p>
            <Button onClick={handleCreate} variant="outline" className="mt-4">
              Créer le premier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map(template => (
            <Card key={template.id} className="bg-zinc-900/50 border-white/10">
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-white">{template.name}</h3>
                      {getStatusBadge(template.status)}
                      {template.category && (
                        <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                          {template.category}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 mb-2">{template.description}</p>
                    <div className="bg-zinc-800 rounded p-3 text-sm text-zinc-300">
                      {template.content}
                    </div>
                    {template.variables?.length > 0 && (
                      <div className="mt-2 flex gap-2">
                        {template.variables.map(v => (
                          <Badge key={v.name} variant="secondary" className="bg-zinc-700 text-zinc-300">
                            {`{{${v.name}}}`}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleStatus(template)}
                      className="text-zinc-400 hover:text-white"
                    >
                      {template.status === "active" ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(template)}
                      className="text-zinc-400 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(template)}
                      className="text-zinc-400 hover:text-zinc-200"
                      title="Archiver"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal création/édition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Modifier le template" : "Nouveau template SMS"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Nom</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Catégorie</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="confirmation, relance, notification..."
                  className="bg-zinc-800 border-white/10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-zinc-800 border-white/10"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">
                Contenu <span className="text-zinc-500">(utilisez {"{{variable}}"} pour les variables)</span>
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => {
                  setFormData({...formData, content: e.target.value});
                  extractVariables(e.target.value);
                }}
                rows={4}
                className="bg-zinc-800 border-white/10"
              />
            </div>

            {variablesList.length > 0 && (
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Variables détectées</label>
                <div className="flex flex-wrap gap-2">
                  {variablesList.map(v => (
                    <Badge key={v.name} className="bg-blue-500/20 text-blue-400">
                      {`{{${v.name}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600">
              {editingTemplate ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// PANEL RÈGLES
// ============================================================

function RulesPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    allowed_senders: ["human:*", "agent:*"],
    time_start: "08:00",
    time_end: "20:00",
    max_per_hour: 10,
    max_per_day: 50
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/communication/rules?channel=sms`);
      setRules(res.data.rules || []);
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setFormData({
      name: "",
      description: "",
      allowed_senders: ["human:*", "agent:*"],
      time_start: "08:00",
      time_end: "20:00",
      max_per_hour: 10,
      max_per_day: 50
    });
    setShowModal(true);
  };

  const handleToggle = async (rule) => {
    try {
      await api.post(`${API}/api/communication/rules/${rule.id}/toggle`);
      loadRules();
    } catch (err) {
      console.error("Erreur:", err);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        channel: "sms",
        allowed_senders: formData.allowed_senders,
        allowed_templates: ["*"],
        time_windows: [{
          start: formData.time_start,
          end: formData.time_end,
          days: [0, 1, 2, 3, 4, 5]
        }],
        rate_limit: {
          max_per_hour: parseInt(formData.max_per_hour),
          max_per_day: parseInt(formData.max_per_day),
          per_recipient: true
        }
      };

      if (editingRule) {
        await api.put(`${API}/api/communication/rules/${editingRule.id}`, payload);
      } else {
        await api.post(`${API}/api/communication/rules?created_by=human:support`, payload);
      }

      setShowModal(false);
      loadRules();
    } catch (err) {
      console.error("Erreur:", err);
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Archiver la règle "${rule.name}" ?`)) return;
    
    try {
      await api.delete(`${API}/api/communication/rules/${rule.id}`);
      toast.success("Règle archivée dans Zoho");
      loadRules();
    } catch (err) {
      console.error("Erreur:", err);
      toast.error("Erreur lors de l'archivage");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Règles d'envoi SMS</h2>
        <Button onClick={handleCreate} className="bg-blue-500 hover:bg-blue-600">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle règle
        </Button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
        </div>
      ) : rules.length === 0 ? (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="py-8 text-center">
            <Settings className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Aucune règle configurée</p>
            <p className="text-sm text-zinc-500 mt-1">Les envois seront autorisés par défaut</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {rules.map(rule => (
            <Card key={rule.id} className={`border ${rule.is_active ? 'bg-zinc-900/50 border-white/10' : 'bg-zinc-900/30 border-zinc-700/50'}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={`font-medium ${rule.is_active ? 'text-white' : 'text-zinc-500'}`}>
                        {rule.name}
                      </h3>
                      <Badge className={rule.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}>
                        {rule.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-400 mb-3">{rule.description}</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-zinc-500">Émetteurs autorisés</p>
                        <p className="text-zinc-300">{rule.allowed_senders?.join(", ")}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Horaires</p>
                        <p className="text-zinc-300">
                          {rule.time_windows?.[0]?.start} - {rule.time_windows?.[0]?.end}
                        </p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Limites</p>
                        <p className="text-zinc-300">
                          {rule.rate_limit?.max_per_hour}/h, {rule.rate_limit?.max_per_day}/jour
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggle(rule)}
                      className="text-zinc-400 hover:text-white"
                    >
                      {rule.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(rule)}
                      className="text-zinc-400 hover:text-zinc-200"
                      title="Archiver"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Nouvelle règle SMS</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Nom</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-zinc-800 border-white/10"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-zinc-800 border-white/10"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Heure début</label>
                <Input
                  type="time"
                  value={formData.time_start}
                  onChange={(e) => setFormData({...formData, time_start: e.target.value})}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Heure fin</label>
                <Input
                  type="time"
                  value={formData.time_end}
                  onChange={(e) => setFormData({...formData, time_end: e.target.value})}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Max / heure</label>
                <Input
                  type="number"
                  value={formData.max_per_hour}
                  onChange={(e) => setFormData({...formData, max_per_hour: e.target.value})}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Max / jour</label>
                <Input
                  type="number"
                  value={formData.max_per_day}
                  onChange={(e) => setFormData({...formData, max_per_day: e.target.value})}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// PANEL HISTORIQUE
// ============================================================

function HistoryPanel({ onRefresh }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    loadLogs();
  }, [filter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/communication/logs?channel=sms&limit=50`;
      if (filter !== "all") {
        url += `&status=${filter}`;
      }
      const res = await api.get(url);
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (logId) => {
    try {
      await api.post(`${API}/api/communication/retry/${logId}?triggered_by=human:support`);
      loadLogs();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Erreur:", err);
    }
  };

  const handleArchive = async (logId, e) => {
    e.stopPropagation();
    try {
      await api.post(`${API}/api/communication/logs/${logId}/archive`);
      loadLogs();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Erreur archivage:", err);
    }
  };

  const handleDelete = async (logId, e) => {
    e.stopPropagation();
    if (!window.confirm("Archiver ce log dans Zoho ?")) return;
    try {
      await api.delete(`${API}/api/communication/logs/${logId}`);
      toast.success("Log archivé dans Zoho");
      loadLogs();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Erreur archivage:", err);
      toast.error("Erreur lors de l'archivage");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case "simulated":
        return <Eye className="w-4 h-4 text-blue-400" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-400" />;
      case "rejected":
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      sent: "Envoyé",
      simulated: "Simulé",
      failed: "Échoué",
      rejected: "Rejeté",
      pending: "En attente"
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="space-y-4">
      {/* Header avec filtres */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Historique des envois</h2>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-zinc-800 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="all" className="text-white">Tous</SelectItem>
              <SelectItem value="sent" className="text-white">Envoyés</SelectItem>
              <SelectItem value="simulated" className="text-white">Simulés</SelectItem>
              <SelectItem value="failed" className="text-white">Échoués</SelectItem>
              <SelectItem value="rejected" className="text-white">Rejetés</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadLogs} className="border-white/10" aria-label="Rafraîchir les logs">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="py-8 text-center">
            <History className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Aucun historique</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map(log => (
            <Card 
              key={log.id} 
              className="bg-zinc-900/50 border-white/10 hover:bg-zinc-800/50 cursor-pointer transition-colors"
              onClick={() => setSelectedLog(log)}
            >
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(log.status)}
                    <div>
                      <p className="text-sm text-white">{log.recipient}</p>
                      <p className="text-xs text-zinc-500">
                        {log.template_name} • {log.triggered_by}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Badge variant="secondary" className={`
                        ${log.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' : ''}
                        ${log.status === 'simulated' ? 'bg-blue-500/20 text-blue-400' : ''}
                        ${log.status === 'failed' ? 'bg-red-500/20 text-red-400' : ''}
                        ${log.status === 'rejected' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                      `}>
                        {getStatusLabel(log.status)}
                      </Badge>
                      <p className="text-xs text-zinc-500 mt-1">{formatDate(log.created_at)}</p>
                    </div>
                    {log.status === "failed" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleRetry(log.id); }}
                        className="text-zinc-400 hover:text-white"
                        title="Réessayer"
                        aria-label="Réessayer l'envoi"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handleDelete(log.id, e)}
                      className="text-zinc-400 hover:text-zinc-200"
                      title="Archiver dans Zoho"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal détails */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails du message</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">Statut</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(selectedLog.status)}
                    <span className="text-white">{getStatusLabel(selectedLog.status)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Mode</p>
                  <p className="text-white">{selectedLog.mode}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Destinataire</p>
                  <p className="text-white">{selectedLog.recipient}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Template</p>
                  <p className="text-white">{selectedLog.template_name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Déclenché par</p>
                  <p className="text-white">{selectedLog.triggered_by}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Date</p>
                  <p className="text-white">{formatDate(selectedLog.created_at)}</p>
                </div>
              </div>

              {selectedLog.context && (
                <div>
                  <p className="text-sm text-zinc-500">Contexte</p>
                  <p className="text-white">{selectedLog.context}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-zinc-500 mb-2">Contenu envoyé</p>
                <div className="bg-zinc-800 rounded-lg p-4 text-sm text-white">
                  {selectedLog.rendered_content}
                </div>
              </div>

              {selectedLog.variables && Object.keys(selectedLog.variables).length > 0 && (
                <div>
                  <p className="text-sm text-zinc-500 mb-2">Variables</p>
                  <div className="bg-zinc-800 rounded-lg p-3 text-xs font-mono">
                    {JSON.stringify(selectedLog.variables, null, 2)}
                  </div>
                </div>
              )}

              {selectedLog.validation && (
                <div>
                  <p className="text-sm text-zinc-500 mb-2">Validation</p>
                  <div className="bg-zinc-800 rounded-lg p-3 text-xs font-mono">
                    {JSON.stringify(selectedLog.validation, null, 2)}
                  </div>
                </div>
              )}

              {selectedLog.error_message && (
                <div>
                  <p className="text-sm text-red-400 mb-2">Erreur</p>
                  <p className="text-red-300">{selectedLog.error_message}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
